import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Image,
  Radio,
  Tag,
} from 'antd';
import { filter, forEach, isEmpty, map } from 'lodash';
import { React, useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../AppContext';
import { ReactComponent as FilterIcon } from '../../../assets/svg/sliders-solid.svg';
import {
  DATE_FORMATS,
  LIMIT,
  REQUEST_STATUS,
  REQUEST_TYPES,
  SIZE,
  SORT,
  STATUS_FILTER,
} from '../../../common/constants';
import { formatDate, hasNonEmptyValueObj } from '../../../common/utils';
import CommonPreview from '../../../components/CommonPreview';
import CommonTable from '../../../components/CommonTable';
import LoaderComponent from '../../../components/LoaderComponent';
import SearchComponent from '../../../components/SearchComponent';
import useInfiniteScroll from '../../../hooks/useInfiniteScroll';
import { CREATE_REQUEST_LIBRARY } from '../graphql/Mutations';
import { BAGS_REQUESTS_LIBRARY } from '../graphql/Queries';
import AllFilterBagReqModal from './AllFilterBagReqModal';
import BagRequestList from './BagRequestList';
import RaiseBagRequestModal from './RaiseBagRequestModal';

const { RangePicker } = DatePicker;
const { Group } = Radio;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialAddressFilter = {
  limit: 10,
  search: '',
  skip: 0,
};

const initialAddressSort = {
  field: 'createdAt',
  order: 'DESC',
};

const BagsTable = ({ isRaiseRequestModalOpen, setIsRaiseRequestModalOpen }) => {
  const {
    state: { isDesktop },
  } = useContext(AppContext);
  const [form] = Form.useForm();
  const [bagReqList, setBagReqList] = useState([]);
  const [bagReqSearchTerm, setBagReqSearchTerm] = useState('');
  const [isEmptyBagReqList, setIsEmptyBagReqList] = useState(false);
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);

  const [isBagReqLoading, setIsBagReqLoading] = useState(true);
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState();
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState();
  const [finalFilterTags, setFinalFilterTags] = useState(null);
  const [isEmptyFilterList, setIsEmptyFilterList] = useState(false);
  const [isRaiseRequestLoading, setIsRaiseRequestLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [sizeImage, setSizeImage] = useState([]);

  const [isAllFilterModalOpen, setIsAllFilterModalOpen] = useState(false);
  const [isFetchMoreBagReqLoading, setIsFetchMoreBagReqLoading] = useState(
    false,
  );
  const [isActive, setIsActive] = useState(true);

  const [requestsLibrary] = useLazyQuery(BAGS_REQUESTS_LIBRARY, {
    onCompleted: (response) => {
      if (isDesktop) {
        setBagReqList([...response?.requestsLibrary?.data]);
      } else {
        setBagReqList([...bagReqList, ...response?.requestsLibrary?.data]);
      }
      if (
        response?.requestsLibrary?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyBagReqList(true);
      } else {
        setIsEmptyBagReqList(false);
      }
      if (response?.requestsLibrary?.data?.length < LIMIT) {
        setIsActive(false);
      }
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.requestsLibrary?.count,
      };
      setPaginationProp(pagination);
      setIsBagReqLoading(false);
      setIsFetchMoreBagReqLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [createRequestLibrary] = useMutation(CREATE_REQUEST_LIBRARY, {
    onError: () => {},
  });

  useEffect(() => {
    setIsBagReqLoading(true);
    setBagReqList([]);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    requestsLibrary({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: LIMIT,
          type: REQUEST_TYPES?.BAGS,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          size: finalWhereFilters?.size,
          libraryIds: finalWhereFilters?.name,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
  }, [isDesktop]);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsBagReqLoading(true);
    setSortedInfo(sorter);
    requestsLibrary({
      variables: {
        filter: {
          ...initialAddressFilter,
          skip,
          limit: pagination?.pageSize,
          search: bagReqSearchTerm,
          type: REQUEST_TYPES?.BAGS,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          size: finalWhereFilters?.size,
          libraryIds: finalWhereFilters?.name,
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setBagReqSearchTerm(trimValue);
    setBagReqList([]);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsBagReqLoading(true);
    requestsLibrary({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          type: REQUEST_TYPES?.BAGS,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          size: finalWhereFilters?.size,
          libraryIds: finalWhereFilters?.name,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
  };

  const handleRaiseRequest = async (values) => {
    setIsRaiseRequestLoading(true);
    const response = await createRequestLibrary({
      variables: {
        data: {
          bagRequest: {
            quantity: parseInt(values?.quantity, 10),
            bagId: values?.size,
          },
          type: REQUEST_TYPES?.BAGS,
        },
      },
    });
    if (response?.data) {
      setBagReqList([]);
      setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
      setIsBagReqLoading(true);
      setIsRaiseRequestModalOpen(false);
      form?.resetFields();
      requestsLibrary({
        variables: {
          filter: {
            ...initialAddressFilter,
            limit: paginationProp?.pageSize || LIMIT,
            search: bagReqSearchTerm,
            type: REQUEST_TYPES?.BAGS,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
            size: finalWhereFilters?.size,
            libraryIds: finalWhereFilters?.name,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialAddressSort,
        },
      });
    }
    setIsRaiseRequestLoading(false);
  };

  const handleFilter = (values) => {
    setIsBagReqLoading(true);
    setIsAllFilterModalOpen(false);
    setBagReqList([]);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsActive(true);
    const filters = {
      status: values?.status,
      createdAt: values?.createdAt?.[1]
        ? { from: values?.createdAt?.[0], to: values?.createdAt?.[1] }
        : null,
      size: values?.size,
    };
    setWhereFilter(filters);
    setFinalWhereFilters(filters);
    requestsLibrary({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: bagReqSearchTerm,
          type: REQUEST_TYPES?.BAGS,
          dateRange: {
            from: values?.createdAt?.[0],
            to: values?.createdAt?.[1],
          },
          size: values?.size,
          status: values?.status,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
  };

  const handleBagScroll = () => {
    if (!isFetchMoreBagReqLoading && !isBagReqLoading) {
      const currentLength = bagReqList?.length;
      setIsFetchMoreBagReqLoading(true);
      requestsLibrary({
        variables: {
          filter: {
            ...initialAddressFilter,
            skip: currentLength,
            limit: paginationProp?.pageSize || LIMIT,
            search: bagReqSearchTerm,
            type: REQUEST_TYPES?.BAGS,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
            size: finalWhereFilters?.size,
            libraryIds: finalWhereFilters?.name,
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialAddressSort,
        },
      });
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks, no-use-before-define
  const infiniteScrollRef = useInfiniteScroll(handleBagScroll, isActive);

  const getFilterData = (confirm) => {
    setIsBagReqLoading(true);
    requestsLibrary({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: bagReqSearchTerm,
          type: REQUEST_TYPES?.BAGS,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          status:
            whereFilter?.status?.length > 0 ? whereFilter?.status : undefined,
          size: whereFilter?.size,
          libraryIds: whereFilter?.name,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    let whereFiltersCopy;
    if (dataIndex === 'size') {
      whereFiltersCopy = {
        ...whereFilter,
        [dataIndex]: null,
      };
    } else {
      whereFiltersCopy = {
        ...whereFilter,
        [dataIndex]: [],
      };
    }
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    setIsBagReqLoading(true);
    requestsLibrary({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: bagReqSearchTerm,
          type: REQUEST_TYPES?.BAGS,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          status:
            whereFiltersCopy?.status?.length > 0
              ? whereFiltersCopy?.status
              : undefined,
          size: whereFiltersCopy?.size,
          libraryIds: whereFiltersCopy?.name,
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setIsEmptyFilterList(false);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      const optionsCopy = [];

      switch (filterIndex) {
        case 'status':
          forEach(STATUS_FILTER, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.key });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        case 'size':
          map(SIZE, (size) => {
            optionsCopy?.push({ label: size?.label, key: size?.value });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      setWhereFilter(finalWhereFilters);
    }
  }, [filterVisible]);

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const onRadioChange = (value, dataIndex) => {
    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: value,
    };
    if (!value?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (dataIndex === 'createdAt') {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      if (dataIndex === 'size') {
        return (
          <div className="custom-radio-filter-dropdown-large-width">
            <Group
              options={map(SIZE, (size) => size)}
              value={whereFilter?.[dataIndex]}
              onChange={(e) => onRadioChange(e?.target?.value, dataIndex)}
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent
            spinning={filterLoading}
            setHeight={35}
            size="medium"
          >
            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div className="filter-checkboxes">
              {filterList?.length > 0
                ? map(filterList, (item) => (
                    <div className="single-checkbox-div" key={item?.key}>
                      <Checkbox
                        value={item}
                        checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                        key={item?.key || item}
                        onChange={(e) => changeFilter(e, dataIndex)}
                        className="single-checkbox"
                      >
                        <span>{item?.label}</span>
                      </Checkbox>
                    </div>
                  ))
                : isEmptyFilterList && (
                    <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
                  )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              id="roles-filter-reset"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              id="roles-filter-ok"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (visible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(visible);
    },
  });

  const columns = [
    {
      title: 'REQUEST ID',
      dataIndex: 'id',
      key: 'id',
      ellipsis: true,
      width: 170,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.requestNumber,
    },
    {
      title: 'QUANTITY',
      dataIndex: 'quantity',
      key: 'quantity',
      ellipsis: true,
      width: 100,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.metaData?.quantity || '-',
    },
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      width: 100,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <div>
          <span className="pr-8">{record?.metaData?.name ?? '-'}</span>
        </div>
      ),
    },
    {
      title: 'SIZE',
      dataIndex: 'size',
      key: 'size',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => {
        const size = record?.metaData?.size;
        return (
          <div>
            {SIZE?.[size] || size ? (
              <>
                <span className="pr-8">{SIZE?.[size]?.label || size}</span>
                <Image
                  preview={{
                    visible: false,
                  }}
                  width={20}
                  height={20}
                  src={SIZE?.[size]?.url || record?.metaData?.image}
                  onClick={(e) => {
                    e?.stopPropagation();
                    setIsVisible(true);
                    setSizeImage(SIZE?.[size]?.url || record?.metaData?.image);
                  }}
                  className="pointer"
                />
              </>
            ) : (
              '-'
            )}
          </div>
        );
      },
      ...filterPopup('size'),
    },
    {
      title: 'REQUEST DATE',
      dataIndex: 'requestDate',
      key: 'requestDate',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('createdAt'),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <Badge
          color={REQUEST_STATUS?.[record?.status]?.color}
          text={REQUEST_STATUS?.[record?.status]?.text}
        />
      ),
      ...filterPopup('status'),
    },
  ];

  const locale = {
    emptyText: isEmptyBagReqList ? '' : <span />,
  };

  return (
    <>
      <CommonPreview
        visible={isVisible}
        setVisible={setIsVisible}
        image={sizeImage}
        setImage={setSizeImage}
      />
      <RaiseBagRequestModal
        form={form}
        onFinish={handleRaiseRequest}
        loadings={isRaiseRequestLoading}
        isModalOpen={isRaiseRequestModalOpen}
        setIsModalOpen={setIsRaiseRequestModalOpen}
      />
      <div className="d-flex justify-end mb-16 align-center">
        <SearchComponent
          className={isDesktop ? 'search-box' : ''}
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
        {!isDesktop && (
          <Badge dot={hasNonEmptyValueObj(finalWhereFilters)} size="small">
            <FilterIcon
              className="mobile-svg-icon ml-16"
              onClick={() => setIsAllFilterModalOpen(true)}
            />
          </Badge>
        )}
      </div>
      {isDesktop ? (
        <CommonTable
          locale={locale}
          columns={columns}
          data={bagReqList || []}
          loading={isBagReqLoading}
          onChange={handleTableChange}
          paginationConfig={paginationProp}
          rowKey={(record) => record?.id}
        />
      ) : (
        <div className="mobile-container-tab no-scroll-bar">
          <AllFilterBagReqModal
            onFinish={handleFilter}
            isModalOpen={isAllFilterModalOpen}
            setIsModalOpen={setIsAllFilterModalOpen}
            finalWhereFilters={finalWhereFilters}
            setFinalWhereFilters={setFinalWhereFilters}
          />
          <BagRequestList
            bagReqList={bagReqList}
            infiniteScrollRef={infiniteScrollRef}
            isFetchMoreBagReqLoading={isFetchMoreBagReqLoading}
            isEmptyBagReqList={isEmptyBagReqList}
            isBagReqLoading={isBagReqLoading}
          />
        </div>
      )}
    </>
  );
};

export default BagsTable;
